import { format } from 'date-fns';
import React, {
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import DatePicker, { type DatePickerProps as ReactDatePickerProps } from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import 'react-datepicker/dist/react-datepicker.css';
import InputError from '../InputError';
import InputHelp from '../InputHelp';
import { Init, InitLabel, InitValue, Wrapper } from './wrapper';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Format a Date into "yyyy-MM-dd" or "yyyy-MM-dd HH:mm" */
const formatDate = (date: Date, showHours?: boolean) =>
  showHours ? format(date, 'yyyy-MM-dd HH:mm') : format(date, 'yyyy-MM-dd');

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * For a single-date picker, we only need to override onChange so that we can
 * call it with (date, name). Everything else is normal ReactDatePickerProps.
 */
export type DatepickerProps = Omit<
  ReactDatePickerProps,
  'onChange' | 'selectsRange' | 'selectsMultiple' | 'showMonthYearDropdown'
> & {
  /** The date that is currently selected in the component. */
  selected?: Date | null;
  /** Name of the field (passed back in onChange). */
  name: string;
  /** Called whenever the user selects a date. */
  onChange: (date: Date | null, name: string) => void;

  /** If true, close the calendar after a user picks a date. */
  shouldCloseOnDateChange?: boolean;

  /** Label to display above/beside the date picker. */
  label?: string | React.ReactNode;
  /** Help text displayed below the field. */
  help?: string;
  /** Error text displayed below the field if any. */
  error?: string;
};

/** Props for the custom input that replaces ReactDatePicker's default text field. */
interface CustomInputProps {
  selected?: Date | null;
  disabled?: boolean;
  showTimeSelect?: boolean;
  label?: string | React.ReactNode;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

// -----------------------------------------------------------------------------
// CustomInput
// -----------------------------------------------------------------------------

/**
 * This replaces the default text input of ReactDatePicker.
 * We show either a formatted date or a placeholder.
 */
const CustomInput = forwardRef<HTMLButtonElement, CustomInputProps>(
  ({ onClick, selected, disabled, showTimeSelect, label }, ref) => {
    const { t } = useTranslation('other');

    return (
      <Init onClick={onClick} ref={ref} disabled={disabled} title={t('selectDate') ?? ''}>
        {/* If you want the label *inside* the button, keep this: */}
        {label && <InitLabel>{label}</InitLabel>}

        <InitValue>
          {selected ? formatDate(selected, showTimeSelect) : (t('selectDate') ?? '')}
        </InitValue>
      </Init>
    );
  },
);
CustomInput.displayName = 'CustomInput';

// -----------------------------------------------------------------------------
// InputDateSingle
// -----------------------------------------------------------------------------

const InputDateSingle = ({
  /* Custom logic props */
  name,
  selected,
  onChange,
  shouldCloseOnDateChange = false,
  label,
  help,
  error,

  /* The rest of props go straight to DatePicker */
  ...rest
}: DatepickerProps) => {
  const datePickerRef = useRef<DatePicker>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Handler: onChange
  // Whenever user picks a date, pass it up as (date, name), optionally close
  // the calendar if shouldCloseOnDateChange = true
  // ---------------------------------------------------------------------------
  const handleDateChange = useCallback(
    (date: Date | null) => {
      onChange(date, name);

      if (date && shouldCloseOnDateChange) {
        datePickerRef.current?.setOpen(false);
      }
    },
    [name, onChange, shouldCloseOnDateChange],
  );

  // ---------------------------------------------------------------------------
  // Effect: If "selected" changes externally, optionally close the calendar
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (datePickerRef.current?.isCalendarOpen && shouldCloseOnDateChange) {
      datePickerRef.current.setOpen(false);
    }
  }, [selected, shouldCloseOnDateChange]);

  return (
    <>
      <Wrapper
        $error={!!error}
        $disabled={rest.disabled}
        $open={isCalendarOpen}
        className={rest.inline ? 'react-datepicker-inline' : undefined}
        style={{ pointerEvents: rest.disabled ? 'none' : undefined }}
      >
        <DatePicker
          ref={datePickerRef}
          selected={selected}
          onChange={handleDateChange}
          /* 
            Provide a single "customInput". We pass relevant props so it can 
            display the label, selected date, etc. 
          */
          customInput={
            <CustomInput
              selected={selected}
              disabled={rest.disabled}
              showTimeSelect={rest.showTimeSelect}
              label={label}
            />
          }
          /* 
            Overwrite onCalendarOpen/onCalendarClose so we can track isCalendarOpen 
            while also allowing user-defined callbacks if desired.
          */
          onCalendarOpen={() => {
            setIsCalendarOpen(true);
            rest.onCalendarOpen?.();
          }}
          onCalendarClose={() => {
            setIsCalendarOpen(false);
            rest.onCalendarClose?.();
          }}
          {...rest} // Pass the rest of the props directly to DatePicker
        />
      </Wrapper>

      {help && <InputHelp disabled={rest.disabled}>{help}</InputHelp>}
      {error && <InputError>{error}</InputError>}
    </>
  );
};

export default InputDateSingle;
